import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Button,
    Card,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    FormControlLabel,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { RouteComponentProps } from 'react-router-dom';
import { Api } from '../../data_sources/api/api';
import { IDocumentShareView } from '../../models/DocumentShare';
import { LoadStatus } from '../common/types';
import { isBlank, getErrorMessage } from '../../utils';
import DocumentExportPanel from './DocumentExportPanel';
import { formatReviewDate } from './documentShareFormat';
import { useDocumentShareStyles, DOC_ICON_FG } from './documentShareStyles';

interface DocumentShareRouteParams {
    region: string;
    token: string;
}

interface DocumentShareViewProps extends RouteComponentProps<DocumentShareRouteParams> {}

function formatDateTime(d?: string | number): string {
    if (!d && d !== 0) return '';
    const date = new Date(d);
    return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleString();
}

/**
 * Public recipient view for a shared compliance document (magic-link flow).
 *
 *  - Fetches the document via GET /document-share/{token}
 *  - Renders name / version / message and downloadable attachments
 *  - When sign-off is required/optional, captures the recipient's name (+ optional
 *    feedback) and a mandatory confirmation tick, then POSTs
 *    /document-share/{token}/acknowledge
 *
 * No auth — the token in the URL is the credential.
 */
const DocumentShareView: React.FC<DocumentShareViewProps> = (props) => {
    const classes = useDocumentShareStyles();
    const { region, token } = props.match.params;

    const [view, setView] = useState<IDocumentShareView | null>(null);
    const [loadStatus, setLoadStatus] = useState<LoadStatus>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [name, setName] = useState('');
    const [feedback, setFeedback] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [ackError, setAckError] = useState<string | null>(null);
    const [acknowledged, setAcknowledged] = useState(false);

    // Fetch the shared document on mount.
    useEffect(() => {
        let active = true;
        async function load() {
            try {
                const api = new Api(region, '');
                const result = await api.getDocumentShare(token);
                if (!active) return;
                setView(result);
                setAcknowledged(result.acknowledged === true);
                setLoadStatus('loaded');
            } catch (e: unknown) {
                if (!active) return;
                setErrorMessage(getErrorMessage(e) || 'Unable to load the document.');
                setLoadStatus('load_error');
            }
        }
        load();
        return () => {
            active = false;
        };
    }, [region, token]);

    const signOff = view?.signOff;
    const showSignOff =
        loadStatus === 'loaded' &&
        !!signOff &&
        signOff !== 'HIDDEN' &&
        !acknowledged;

    async function handleAcknowledge() {
        setAckError(null);
        if (isBlank(name)) {
            setAckError('Please enter your name.');
            return;
        }
        if (!confirmed) {
            setAckError('Please tick the box to confirm you have read this document.');
            return;
        }
        try {
            setSubmitting(true);
            const api = new Api(region, '');
            await api.acknowledgeDocumentShare(token, {
                name: name.trim(),
                feedback: view?.enableFeedback && !isBlank(feedback)
                    ? feedback.trim()
                    : undefined,
            });
            // Re-fetch so we render the server's acknowledgement (authoritative
            // date/name/feedback). Best-effort: still mark acknowledged if it fails.
            try {
                const refreshed = await api.getDocumentShare(token);
                setView(refreshed);
            } catch (_) {
                // keep the current view
            }
            setAcknowledged(true);
        } catch (e: unknown) {
            setAckError(getErrorMessage(e) || 'Unable to record your acknowledgement.');
        } finally {
            setSubmitting(false);
        }
    }

    if (loadStatus === 'loading') {
        return (
            <Container className={classes.center} maxWidth="sm">
                <CircularProgress />
                <Typography variant="h6" style={{ marginTop: 16 }}>
                    Loading document…
                </Typography>
            </Container>
        );
    }

    if (loadStatus === 'load_error' || !view) {
        return (
            <Container className={classes.page} maxWidth="md">
                <Grid container className={classes.wrap}>
                    <div className={classes.stack}>
                        <Card variant="outlined" className={classes.hero}>
                            <Alert severity="error">{errorMessage}</Alert>
                        </Card>
                    </div>
                </Grid>
            </Container>
        );
    }

    const attachments = view.attachments ?? [];
    const ack = view.acknowledgement;

    return (
        <Container className={classes.page} maxWidth="md">
            <Grid container className={classes.wrap}>
                <div className={classes.stack}>
                    {/* Hero */}
                    <Card variant="outlined" className={classes.hero}>
                        <div className={classes.heroRow}>
                            <Avatar className={classes.heroAvatar} variant="rounded">
                                <DescriptionOutlinedIcon style={{ color: DOC_ICON_FG }} />
                            </Avatar>
                            <div className={classes.heroBody}>
                                <Typography className={classes.eyebrow}>
                                    {view.documentType || 'Document'}
                                </Typography>
                                <h1 className={classes.heroTitle}>{view.documentName}</h1>
                                {view.versionNumber && (
                                    <div className={classes.metaRow}>
                                        <Chip
                                            size="small"
                                            className={classes.versionChip}
                                            label={`v${view.versionNumber}`}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    {acknowledged && (
                        <Alert severity="success">
                            {ack ? (
                                <>
                                    Acknowledged
                                    {ack.name ? ` by ${ack.name}` : ''}
                                    {ack.date ? ` on ${formatDateTime(ack.date)}` : ''}.
                                    {ack.feedback && (
                                        <div style={{ marginTop: 4, fontStyle: 'italic' }}>
                                            “{ack.feedback}”
                                        </div>
                                    )}
                                </>
                            ) : (
                                'You have acknowledged this document.'
                            )}
                        </Alert>
                    )}

                    {/* Message */}
                    {view.message && (
                        <Card variant="outlined" className={classes.sectionCard}>
                            <div className={classes.panelHeaderBar}>
                                <Typography className={classes.panelHeading}>
                                    Message
                                </Typography>
                            </div>
                            <div className={classes.panelBody}>
                                <Typography className={classes.message}>
                                    {view.message}
                                </Typography>
                            </div>
                        </Card>
                    )}

                    {/* Details */}
                    {(view.versionDescription ||
                        view.versionNotes ||
                        view.reviewDate) && (
                        <Card variant="outlined" className={classes.sectionCard}>
                            <div className={classes.panelHeaderBar}>
                                <Typography className={classes.panelHeading}>
                                    Details
                                </Typography>
                            </div>
                            <div className={classes.panelBody}>
                                {view.versionDescription && (
                                    <div className={classes.detailGroup}>
                                        <Typography className={classes.detailLabel}>
                                            Description
                                        </Typography>
                                        <Typography className={classes.detailText}>
                                            {view.versionDescription}
                                        </Typography>
                                    </div>
                                )}
                                {view.versionNotes && (
                                    <div className={classes.detailGroup}>
                                        <Typography className={classes.detailLabel}>
                                            Version notes
                                        </Typography>
                                        <Typography className={classes.detailText}>
                                            {view.versionNotes}
                                        </Typography>
                                    </div>
                                )}
                                {view.reviewDate && (
                                    <div className={classes.detailGroup}>
                                        <Typography className={classes.detailLabel}>
                                            Review date
                                        </Typography>
                                        <Typography className={classes.detailText}>
                                            {formatReviewDate(view.reviewDate)}
                                        </Typography>
                                    </div>
                                )}
                            </div>
                        </Card>
                    )}

                    {/* Attachments */}
                    <Card variant="outlined" className={classes.sectionCard}>
                        <div className={classes.panelHeaderBar}>
                            <Typography className={classes.panelHeading}>
                                Attachments
                            </Typography>
                        </div>
                        <div className={classes.panelBody}>
                            <DocumentExportPanel
                                view={view}
                                attachments={attachments}
                                urlFor={(id) =>
                                    new Api(region, '').documentShareAttachmentUrl(
                                        token,
                                        id
                                    )
                                }
                            />
                        </div>
                    </Card>

                    {/* Sign-off */}
                    {showSignOff && (
                        <Card variant="outlined" className={classes.sectionCard}>
                            <div className={classes.panelHeaderBar}>
                                <Typography className={classes.panelHeading}>
                                    Acknowledgement
                                    {signOff === 'REQUIRED' ? ' (required)' : ' (optional)'}
                                </Typography>
                            </div>
                            <div className={classes.panelBody}>
                                <Typography variant="body2" className={classes.ackIntro}>
                                    Please confirm you have read and understood this document.
                                </Typography>

                                {ackError && (
                                    <Alert
                                        severity="error"
                                        style={{ marginBottom: 16 }}
                                        onClose={() => setAckError(null)}
                                    >
                                        {ackError}
                                    </Alert>
                                )}

                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Your name"
                                            variant="outlined"
                                            fullWidth
                                            required
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            data-testid="document-share-name-input"
                                        />
                                    </Grid>
                                    {view.enableFeedback && (
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Feedback (optional)"
                                                variant="outlined"
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                value={feedback}
                                                onChange={(e) => setFeedback(e.target.value)}
                                                inputProps={{ maxLength: 2000 }}
                                                data-testid="document-share-feedback-input"
                                            />
                                        </Grid>
                                    )}
                                </Grid>

                                <div
                                    className={
                                        confirmed
                                            ? `${classes.confirmBox} ${classes.confirmBoxChecked}`
                                            : classes.confirmBox
                                    }
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="primary"
                                                checked={confirmed}
                                                onChange={(e) =>
                                                    setConfirmed(e.target.checked)
                                                }
                                                data-testid="document-share-confirm-checkbox"
                                            />
                                        }
                                        label="I confirm I have read and understood this document."
                                    />
                                </div>

                                <div className={classes.buttonWrapper}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        disableElevation
                                        disabled={submitting || !confirmed || isBlank(name)}
                                        onClick={handleAcknowledge}
                                        data-testid="document-share-acknowledge-button"
                                        className={classes.greenButton}
                                    >
                                        Acknowledge
                                    </Button>
                                    {submitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </div>
                            </div>
                        </Card>
                    )}
                </div>
            </Grid>
        </Container>
    );
};

export default DocumentShareView;
